import {useEffect, useState} from 'react';
import {animate, dynamic, isServer, toIntent} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {css} from '../css';

const visualViewport = !isServer ? window.visualViewport : undefined;

type ViewportSize = {
  width: number;
  height: number;
};

function getViewportSize(): ViewportSize {
  return {
    // eslint-disable-next-line @sail/ui-no-reflow
    width: visualViewport?.width || window.innerWidth,
    // eslint-disable-next-line @sail/ui-no-reflow
    height: visualViewport?.height || window.innerHeight,
  };
}

// Use visualViewport api to track viewport available height when iOS virtual keyboard is open
function useViewportArea(): ViewportSize {
  const [size, setSize] = useState(() =>
    isServer ? {width: 0, height: 0} : getViewportSize(),
  );

  useEffect(() => {
    const onResize = () => {
      setSize(getViewportSize());
    };

    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);

    return () => {
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);

  return size;
}

export type MobileDisplay = 'bottom-sheet' | 'fullscreen-sheet';

export const bottomSheetStyles = dynamic(() => {
  const {height} = useViewportArea();

  const keyboardHeight =
    // If we are in an iframe (Connect) we can't get parent's visualViewport
    // due to Same-Origin Policy. In that case, we ignore viewport's size.
    window.parent !== window
      ? `max(env(safe-area-inset-bottom), 0px)`
      : `max(100dvh - ${height}px, env(safe-area-inset-bottom), 0px)`;

  return [
    css({
      '--keyboard-height': keyboardHeight,
      position: 'fixed',
      bottom: 'space.0',
      top: 'initial',
      right: 'initial',
      left: 'initial',
      width: '100vw',
      borderWidth: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      borderTopLeftRadius: $['space.150'],
      borderTopRightRadius: $['space.150'],
      maxHeight:
        'calc(100dvh - env(safe-area-inset-bottom, 0) - env(safe-area-inset-top, 0) - 40px)',
      boxShadow: 'overlay.inverted',
      transform: 'translateY(0%)',
      transition:
        'box-shadow 200ms ease-in-out 150ms, padding-bottom 250ms ease-out',
      paddingBottom: 'var(--keyboard-height)',
      backgroundColor: 'surface',
    }),
  ];
});

export const fullscreenSheetStyles = css({
  width: 'fill',
  height:
    'calc(100% - var(--sail-layer-margin-top, 0px) - var(--sail-layer-margin-bottom, 0px))',
  borderRadius: '0px',
  maxHeight: 'fill',
  borderWidth: '0px',
  top: 'var(--sail-layer-margin-top, 0px)',
  boxShadow: 'none',
});

export const fullscreenAnimation = (disabled: boolean) =>
  animate.inout({
    duration: 250,
    opacity: 0,
    transform: 'scale(0.95, 0.95)',
    easing: 'ease-in-out',
    disabled,
  });

export const bottomSheetAnimation = (disabled: boolean) =>
  toIntent([
    animate.inout({
      duration: 250,
      transform: 'translateY(100%)',
      easing: 'ease-out',
      disabled,
    }),
  ]);
